import { useI18n } from '../../../i18n/I18nProvider';
import { cn } from '../../../lib/cn';

// El embudo de ventas visual (ciclo-metricas-embudo-y-contenido, rehecho el 28/09
// tras el primer intento con `FunnelChart` de recharts — quedó feo). Un embudo real:
// un polígono continuo por banda (clip-path), ancho proporcional a la raíz del
// conteo (para que un valor chico pero > 0 siga siendo visible).
//
// Rediseñado el 30/09 (D-034): las etapas reciben el conteo ACUMULADO
// (`porEtapaAcumulado`), y el "% avanza" pasó de una franja que atravesaba todo
// el ancho a una columna al costado.
//
// Segunda vuelta el mismo día (D-035), pedido de Javier tras ver el primer
// rediseño: color en degradé (rojo pastel -> verde pastel sólido) de la etapa 1
// a la 5 en vez del degradé de marca; las insignias de "% avanza" pasan a
// engancharse exactamente en la INTERSECCIÓN entre dos etapas (no en el medio de
// una), con un punto rojo + una línea que señalan a qué costura corresponden; y
// debajo del embudo, un pie con 3 indicadores (Ganados/Perdidos/En proceso) para
// saber cómo terminaron los que llegaron a la etapa 5.

interface Etapa {
  n: number;
  label: string;
  count: number;
}

const ALTURA_BANDA = 88;
const ANCHO_MIN_PCT = 12;
const ANCHO_COSTADO = 150;
const GAP_COSTADO = 30;

function anchoPct(count: number, base: number): number {
  if (base <= 0 || count <= 0) return 0;
  return Math.max(ANCHO_MIN_PCT, Math.min(100, Math.sqrt(count / base) * 100));
}

// Rojo pastel (etapa 1) -> verde pastel más saturado/"sólido" (etapa 5).
function colorEtapa(i: number, total: number): string {
  const t = total > 1 ? i / (total - 1) : 1;
  const hue = 120 * t;
  const sat = 42 + 16 * t;
  const light = 82 - 24 * t;
  return `hsl(${String(Math.round(hue))}, ${String(Math.round(sat))}%, ${String(Math.round(light))}%)`;
}

type TonoFranja = 'verde' | 'rojo' | 'gris' | 'ambar';

interface Franja {
  tono: TonoFranja;
  pctTexto: string;
  detalleTexto: string;
}

function franjaInfo(
  desde: number,
  hasta: number,
  t: { enRango: string; revisar: string; muestraChica: string; vigilar: string; avanza: string },
): Franja | null {
  if (desde <= 0) return null;
  const tasa = Math.round((hasta / desde) * 100);
  if (tasa === 0) {
    return { tono: 'ambar', pctTexto: `0% ${t.avanza}`, detalleTexto: `${t.vigilar} (n=${String(desde)})` };
  }
  if (desde < 5) {
    return {
      tono: 'gris',
      pctTexto: `${String(tasa)}% ${t.avanza}`,
      detalleTexto: `${t.muestraChica} (n=${String(desde)})`,
    };
  }
  if (tasa >= 30) return { tono: 'verde', pctTexto: `${String(tasa)}% ${t.avanza}`, detalleTexto: t.enRango };
  return { tono: 'rojo', pctTexto: `${String(tasa)}% ${t.avanza}`, detalleTexto: t.revisar };
}

const TONO_CLASE: Record<TonoFranja, string> = {
  verde: 'bg-success text-white',
  rojo: 'bg-danger text-white',
  ambar: 'bg-warning text-marca-negro',
  gris: 'bg-marca-gris-medio text-marca-negro',
};

function IndicadorPie({
  puntoClase,
  etiqueta,
  valor,
}: {
  puntoClase: string;
  etiqueta: string;
  valor: number;
}): JSX.Element {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', puntoClase)} aria-hidden="true" />
      <span className="font-bold tabular-nums text-text-strong">{valor}</span>
      <span className="text-text-subtle">{etiqueta}</span>
    </div>
  );
}

export function Embudo({
  etapas,
  cierre,
}: {
  /** Las etapas de trabajo (Contacto → Propuesta), YA con el conteo acumulado. */
  etapas: Etapa[];
  /** El paso final: cuántos llegaron a la etapa 5, más cómo terminaron. */
  cierre: {
    label: string;
    count: number;
    desglose: { ganados: number; perdidos: number; enProceso: number };
  };
}): JSX.Element {
  const es = useI18n();
  const t = es.embudo;
  const base = etapas[0]?.count ?? 0;
  const total = base;

  const bandas = [
    ...etapas.map((e) => ({ ...e })),
    { n: cierre.count > 0 ? etapas.length + 1 : -1, label: cierre.label, count: cierre.count },
  ];
  const alturaTotal = bandas.length * ALTURA_BANDA;
  const esCierreVacio = cierre.count === 0;

  // Ancho de cada banda (arriba/abajo), calculado UNA vez: lo usan tanto el
  // polígono de la banda como el puntero de la insignia, que tiene que nacer
  // justo en el borde real del embudo en esa costura (no en un punto fijo —
  // Javier lo marcó: con bandas angostas el punto quedaba flotando, sin tocar
  // el embudo).
  const anchos = bandas.map((etapa, i) => {
    const anterior = i > 0 ? bandas[i - 1]! : null;
    return {
      topW: anterior ? anchoPct(anterior.count, base) : anchoPct(etapa.count, base),
      botW: anchoPct(etapa.count, base),
    };
  });

  return (
    <div className="overflow-hidden rounded-lg border border-marca-neutro bg-surface p-3 shadow-card">
      <div className="flex" style={{ gap: GAP_COSTADO }}>
        {/* ---- El embudo ---- */}
        <div className="relative flex-1" style={{ height: alturaTotal }}>
          {bandas.map((etapa, i) => {
            const { topW, botW } = anchos[i]!;
            const esUltimaVacia = i === bandas.length - 1 && esCierreVacio;
            // Punto de la costura: el borde derecho real del embudo en esa altura
            // (mismo valor que usa el polígono para el lado derecho de arriba).
            const bordeCosturaPct = 50 + topW / 2;

            return (
              <div
                key={i}
                className="absolute inset-x-0"
                style={{ top: i * ALTURA_BANDA, height: ALTURA_BANDA }}
              >
                {/* Grupo hover (D-037, pedido de Javier): la banda entera -- forma +
                    texto, juntas -- se resalta y se levanta un poco al pasar el mouse. */}
                <div
                  className={cn(
                    'absolute inset-0',
                    !esUltimaVacia &&
                      'transition-transform duration-150 hover:-translate-y-0.5 hover:brightness-95',
                  )}
                >
                  <div
                    className={cn(
                      'absolute inset-0 mx-auto',
                      esUltimaVacia && 'border-2 border-dashed border-marca-primario-claro bg-transparent',
                    )}
                    style={{
                      ...(esUltimaVacia ? {} : { background: colorEtapa(i, bandas.length) }),
                      clipPath: `polygon(${String(50 - topW / 2)}% 0%, ${String(50 + topW / 2)}% 0%, ${String(50 + (esUltimaVacia ? 0 : botW) / 2)}% 100%, ${String(50 - (esUltimaVacia ? 0 : botW) / 2)}% 100%)`,
                    }}
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-center px-3 text-center">
                    {esUltimaVacia ? (
                      <>
                        <p className="text-sm font-semibold text-text-strong">{etapa.label} — 0</p>
                        <p className="mt-1 text-xs text-text-subtle">{t.sinCierres}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-bold leading-tight text-marca-negro">
                          {etapa.n > 0 ? `${t.etapaPrefijo} ${String(etapa.n)}: ` : ''}
                          {etapa.label}
                        </p>
                        <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-black/25 px-3 py-1 text-xs font-semibold tabular-nums text-white backdrop-blur-sm">
                          <span>
                            {etapa.count} {t.leads}
                          </span>
                          <span className="opacity-50" aria-hidden="true">
                            ·
                          </span>
                          <span>{total > 0 ? Math.round((etapa.count / total) * 100) : 0}%</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {/* Puntero de la insignia (si esta banda es el destino de una transición):
                    nace pegado al borde real del embudo y cruza el hueco hasta la insignia. */}
                {i > 0 && franjaInfo(bandas[i - 1]!.count, etapa.count, t) && (
                  <>
                    <span
                      className="absolute h-2.5 w-2.5 rounded-full bg-danger ring-2 ring-surface"
                      style={{
                        top: 0,
                        left: `${String(bordeCosturaPct)}%`,
                        transform: 'translate(-50%, -50%)',
                      }}
                      aria-hidden="true"
                    />
                    <span
                      className="absolute bg-marca-gris-medio"
                      style={{
                        top: 0,
                        left: `${String(bordeCosturaPct)}%`,
                        width: `calc(${String(100 - bordeCosturaPct)}% + ${String(GAP_COSTADO - 6)}px)`,
                        height: 1.5,
                        transform: 'translateY(-50%)',
                      }}
                      aria-hidden="true"
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* ---- Las insignias de "% avanza" ---- */}
        <div className="relative shrink-0" style={{ width: ANCHO_COSTADO, height: alturaTotal }}>
          {bandas.map((etapa, i) => {
            if (i === 0) return null;
            const anterior = bandas[i - 1]!;
            const franja = franjaInfo(anterior.count, etapa.count, t);
            if (!franja) return null;
            const costuraY = i * ALTURA_BANDA;

            return (
              <div
                key={i}
                className={cn(
                  'absolute w-full rounded-md px-2.5 py-1.5 text-center leading-tight shadow-sm',
                  TONO_CLASE[franja.tono],
                )}
                style={{ top: costuraY, transform: 'translateY(-50%)' }}
              >
                <p className="text-xs font-bold">{franja.pctTexto}</p>
                <p className="text-[10px] opacity-90">{franja.detalleTexto}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ---- El pie: de los que llegaron a la etapa 5, cómo terminaron ---- */}
      {!esCierreVacio && (
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-marca-neutro pt-3">
          <IndicadorPie puntoClase="bg-success" etiqueta={t.ganados} valor={cierre.desglose.ganados} />
          <IndicadorPie puntoClase="bg-danger" etiqueta={t.perdidos} valor={cierre.desglose.perdidos} />
          <IndicadorPie
            puntoClase="bg-marca-gris-medio"
            etiqueta={t.enProceso}
            valor={cierre.desglose.enProceso}
          />
        </div>
      )}
    </div>
  );
}
